import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Navigation, MapPin, Clock, Car, Truck, Accessibility, 
  Bell, LogOut, Phone, User, Play, CheckCircle, MapPinned,
  AlertCircle, Send, Calendar, MessageSquare, ExternalLink,
  PhoneCall, Shield, Trophy, Star, Award, Banknote, DollarSign
} from "lucide-react";
import NotificationPanel from "@/components/NotificationPanel";
import SMSLogPanel from "@/components/SMSLogPanel";
import MockMap from "@/components/MockMap";
import ConfettiCelebration from "@/components/ConfettiCelebration";
import RideProgressTimeline from "@/components/RideProgressTimeline";
import LiveFleetMap from "@/components/LiveFleetMap";
import TripControls from "@/components/TripControls";
import { phaseLabel, etaTargetLabel } from "@/lib/tripPhase";
import CalendarView from "@/components/calendar/CalendarView";
import RideDetailDialog from "@/components/calendar/RideDetailDialog";

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [currentRide, setCurrentRide] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSMSLog, setShowSMSLog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [locationNotes, setLocationNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [preferredNav, setPreferredNav] = useState(localStorage.getItem("preferredNav") || "google");
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("");
  const [showEmergencyContact, setShowEmergencyContact] = useState(false);
  const [myStats, setMyStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [calendarRideId, setCalendarRideId] = useState(null);

  const latestPos = useRef(null);
  const activeRideId = useRef(null);
  activeRideId.current = currentRide?.id || null;

  // Live GPS: every 5s during an active trip, every 30s otherwise
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        latestPos.current = position.coords;
        setDriverLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      (error) => console.error("Geolocation error:", error),
      { enableHighAccuracy: true, maximumAge: 3000 }
    );
    let lastIdlePost = 0;
    const send = async () => {
      const c = latestPos.current;
      if (!c) return;
      try {
        if (activeRideId.current) {
          await apiClient.post(`/rides/${activeRideId.current}/location`, { latitude: c.latitude, longitude: c.longitude });
        } else if (Date.now() - lastIdlePost >= 30000) {
          lastIdlePost = Date.now();
          await apiClient.post("/drivers/location", { latitude: c.latitude, longitude: c.longitude, heading: c.heading, speed: c.speed });
        }
      } catch (error) {
        console.error("Failed to update location:", error);
      }
    };
    const timer = setInterval(send, 5000);
    return () => { navigator.geolocation.clearWatch(watchId); clearInterval(timer); };
  }, []);

  // Keep the screen awake during an active trip so GPS keeps flowing
  useEffect(() => {
    if (!currentRide?.id || !("wakeLock" in navigator)) return;
    let lock;
    const acquire = async () => { try { lock = await navigator.wakeLock.request("screen"); } catch (e) { console.warn("Wake lock unavailable", e); } };
    const onVisible = () => document.visibilityState === "visible" && acquire();
    acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => { document.removeEventListener("visibilitychange", onVisible); lock?.release?.(); };
  }, [currentRide?.id]);

  useEffect(() => {
    fetchRides();
    fetchNotifications();
    const interval = setInterval(() => {
      fetchRides();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const openNavigation = (address, lat, lng, provider = preferredNav) => {
    let url;
    if (provider === "waze") {
      if (lat && lng) {
        url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
      } else {
        url = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
      }
    } else {
      if (lat && lng) {
        url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      } else {
        url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
      }
    }
    window.open(url, "_blank");
  };

  const setNavPreference = (provider) => {
    setPreferredNav(provider);
    localStorage.setItem("preferredNav", provider);
    toast.success(`Navigation preference set to ${provider === "google" ? "Google Maps" : "Waze"}`);
  };

  const fetchRides = async () => {
    try {
      const response = await apiClient.get("/rides");
      setRides(response.data);
      
      // Find current active ride (in_progress or arrived)
      const active = response.data.find(r => 
        ["in_progress", "arrived"].includes(r.status)
      );
      setCurrentRide(active);
    } catch (error) {
      console.error("Failed to fetch rides:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get("/notifications");
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Fetch driver's own stats for leaderboard
  const fetchMyStats = async () => {
    try {
      const response = await apiClient.get("/drivers/my-stats");
      setMyStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleAcceptRide = async (rideId) => {
    setIsLoading(true);
    try {
      await apiClient.post(`/rides/${rideId}/accept`);
      toast.success("Ride accepted! Client has been notified.");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept ride");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartRide = async (ride) => {
    setIsLoading(true);
    try {
      const p = ride.pickup_locations?.[0] || {};
      const startReq = apiClient.post(`/rides/${ride.id}/start`);
      openNavigation(p.address, p.latitude, p.longitude);
      await startReq;
      toast.success("Navigating to pickup. Client can now track you live.");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start ride");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCashReceived = async (rideId) => {
    setIsLoading(true);
    try {
      await apiClient.post(`/rides/${rideId}/cash-received`);
      toast.success("Cash payment confirmed!");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm cash payment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLocation = async () => {
    if (!currentRide) return;
    setIsLoading(true);
    try {
      const c = latestPos.current;
      if (!c) {
        toast.error("Waiting for GPS signal");
        return;
      }
      await apiClient.post(`/rides/${currentRide.id}/location`, {
        latitude: c.latitude,
        longitude: c.longitude,
        notes: locationNotes
      });
      toast.success("Location updated");
      setLocationNotes("");
      fetchRides();
    } catch (error) {
      toast.error("Failed to update location");
    } finally {
      setIsLoading(false);
    }
  };

  const TRIP_TOASTS = {
    arrived: "Arrived! Wait timer started and client notified.",
    "start-trip": "Trip started. Wait timer stopped.",
    "start-waiting": "Wait timer started.",
    "resume-trip": "Trip resumed. Wait timer stopped.",
  };

  const handleTripAction = async (action) => {
    if (!currentRide) return;
    setIsLoading(true);
    try {
      const { data } = await apiClient.post(`/rides/${currentRide.id}/${action}`);
      if (action === "complete") {
        const extra = data.wait_charge ? ` Final fare $${data.total_fare.toFixed(2)} incl. $${data.wait_charge.toFixed(2)} wait time.` : "";
        setCelebrationMessage(`You got ${currentRide.client_name} there safely!${extra}`);
        setShowCelebration(true);
        setCurrentRide(null);
      } else {
        toast.success(TRIP_TOASTS[action]);
      }
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle calling emergency contact
  const handleEmergencyCall = (contactPhone, contactName) => {
    if (contactPhone) {
      window.location.href = `tel:${contactPhone}`;
      toast.info(`Calling emergency contact: ${contactName || 'Unknown'}`);
    } else {
      toast.error("No emergency contact phone number available");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      in_progress: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      arrived: "bg-lime-500/20 text-lime-400 border-lime-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30"
    };
    return colors[status] || "bg-slate-500/20 text-slate-400";
  };

  const vehicleIcons = { car: Car, van: Truck, handicap_van: Accessibility };
  
  // Scheduled rides (not yet started)
  const scheduledRides = rides.filter(r => r.status === "scheduled");
  // Completed rides
  const completedRides = rides.filter(r => r.status === "completed");

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-20 md:pb-8">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40 safe-area-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-lime-400 rounded-xl flex items-center justify-center">
                <Navigation className="w-4 h-4 md:w-5 md:h-5 text-slate-900" />
              </div>
              <div>
                <span className="text-lg md:text-xl font-heading font-black bg-gradient-to-r from-lime-300 via-white to-cyan-300 bg-clip-text text-transparent">There You Are</span>
                <Badge variant="outline" className="ml-2 border-lime-400/30 text-lime-400 text-xs">Driver</Badge>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              {/* My Stats Button */}
              <Button
                variant="ghost"
                size="icon"
                className="relative text-amber-400 hover:text-amber-300 w-9 h-9"
                onClick={() => {
                  fetchMyStats();
                  setShowStats(true);
                }}
                data-testid="driver-stats-button"
              >
                <Trophy className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-slate-400 hover:text-white w-9 h-9"
                onClick={() => setShowSMSLog(true)}
                data-testid="driver-sms-button"
              >
                <MessageSquare className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-slate-400 hover:text-white w-9 h-9"
                onClick={() => setShowNotifications(true)}
                data-testid="driver-notifications-button"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-lime-400 text-slate-900 text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
                <User className="w-4 h-4 text-lime-400" />
                <span className="text-sm font-medium">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-slate-400 hover:text-white w-9 h-9"
                data-testid="driver-logout-button"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        {/* Current Ride - HUD Style */}
        {currentRide && (
          <Card className="mb-6 bg-slate-900 border-slate-800 overflow-hidden" data-testid="current-ride-card">
            <div className="relative">
              {/* Live Map */}
              <div className="h-48 md:h-64 relative">
                <LiveFleetMap 
                  mode="driver"
                  currentRide={currentRide}
                  driverLocation={driverLocation}
                  height="100%"
                  variant="dark"
                  showControls={true}
                />
                {/* ETA Badge */}
                {currentRide.eta_minutes != null && currentRide.status === "in_progress" && (
                  <div className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur px-4 py-2 rounded-lg border border-lime-400/30 z-[1000]" data-testid="driver-eta-badge">
                    <p className="text-lime-400 text-sm font-medium">ETA {etaTargetLabel(currentRide)}</p>
                    <p className="text-2xl font-heading font-bold text-white">{currentRide.eta_minutes} min</p>
                    {currentRide.distance_remaining_miles != null && (
                      <p className="text-xs text-slate-400">{currentRide.distance_remaining_miles} mi left</p>
                    )}
                  </div>
                )}
              </div>

              {/* Ride Progress Timeline - Compact */}
              <div className="px-4 pt-4">
                <RideProgressTimeline 
                  ride={currentRide}
                  variant="dark"
                  compact={true}
                  showDriver={false}
                  showETA={false}
                />
              </div>

              {/* Ride Info HUD */}
              <div className="p-4 md:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={getStatusColor(currentRide.status)} data-testid="driver-trip-phase-badge">
                      {phaseLabel(currentRide).toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm">Client PIN</p>
                    <p className="text-2xl font-mono font-bold text-lime-400 tracking-widest">{currentRide.pin_code}</p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="bg-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{currentRide.client_name}</p>
                        <p className="text-slate-400 text-sm">{currentRide.client_phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-lime-400/30 text-lime-400 hover:bg-lime-400/10"
                        onClick={() => window.open(`tel:${currentRide.client_phone}`)}
                        data-testid="call-client-button"
                      >
                        <Phone className="w-4 h-4 mr-1" /> Call
                      </Button>
                      {/* Emergency Contact Button */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                        onClick={() => setShowEmergencyContact(!showEmergencyContact)}
                        data-testid="emergency-contact-button"
                      >
                        <Shield className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Emergency Contact Panel */}
                  {showEmergencyContact && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-xs uppercase tracking-wide mb-2 font-semibold">Emergency Contact</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{currentRide.emergency_contact_name || "Family Contact"}</p>
                          <p className="text-slate-400 text-sm">{currentRide.emergency_contact_phone || currentRide.client_phone}</p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-red-500 hover:bg-red-600 text-white"
                          onClick={() => handleEmergencyCall(
                            currentRide.emergency_contact_phone || currentRide.client_phone,
                            currentRide.emergency_contact_name
                          )}
                          data-testid="call-emergency-button"
                        >
                          <PhoneCall className="w-4 h-4 mr-1" /> Call Now
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Ride Sharing Preference */}
                  {currentRide.ride_sharing_preference && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      currentRide.ride_sharing_preference === 'shared' 
                        ? 'bg-green-500/20 border border-green-500/30' 
                        : 'bg-rose-500/20 border border-rose-500/30'
                    }`}>
                      <p className={`text-sm font-medium ${
                        currentRide.ride_sharing_preference === 'shared' ? 'text-green-300' : 'text-rose-300'
                      }`}>
                        {currentRide.ride_sharing_preference === 'shared' 
                          ? '👥 Client is OK with sharing ride' 
                          : '👤 Client prefers to ride alone'}
                      </p>
                    </div>
                  )}
                  
                  {/* Accessibility Needs for Active Ride */}
                  {(currentRide.mobility_aids?.length > 0 || currentRide.assistance_needs?.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      {currentRide.mobility_aids?.length > 0 && (
                        <p className="text-amber-400 text-sm mb-1">
                          <Accessibility className="w-4 h-4 inline mr-1" />
                          <span className="font-semibold">Client has:</span>{' '}
                          {currentRide.mobility_aids.map(aid => {
                            const labels = {
                              'wheelchair': 'Wheelchair',
                              'cane': 'Cane',
                              'walker': 'Walker',
                              'crutches': 'Crutches',
                              'motorized_wheelchair': 'Motorized Wheelchair',
                              'scooter': 'Scooter'
                            };
                            return labels[aid] || aid.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                          }).join(', ')}
                        </p>
                      )}
                      {currentRide.assistance_needs?.length > 0 && (
                        <p className="text-blue-400 text-sm">
                          <span className="font-semibold">Client needs:</span>{' '}
                          {currentRide.assistance_needs.map(need => {
                            const labels = {
                              'step_stool': 'Step Stool',
                              'entry_exit_help': 'Help Getting In/Out of Vehicle',
                              'blind': 'Visual Assistance',
                              'deaf': 'Hearing Assistance',
                              'oxygen': 'Oxygen Tank Assistance',
                              'service_animal': 'Service Animal Accommodation'
                            };
                            return labels[need] || need.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                          }).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Locations */}
                <div className="space-y-3">
                  {currentRide.pickup_locations.map((loc, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-400 text-xs uppercase tracking-wide">Pickup {index + 1}</p>
                        <p className="font-medium text-white">{loc.address}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10"
                        onClick={() => openNavigation(loc.address, loc.latitude, loc.longitude)}
                        data-testid={`navigate-pickup-${index}`}
                      >
                        <Navigation className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="w-3 h-3" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Dropoff</p>
                      <p className="font-medium text-white">{currentRide.dropoff_address}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-orange-400/30 text-orange-400 hover:bg-orange-400/10"
                      onClick={() => openNavigation(currentRide.dropoff_address, currentRide.dropoff_latitude, currentRide.dropoff_longitude)}
                      data-testid="navigate-dropoff"
                    >
                      <Navigation className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Navigation Preference */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <span className="text-slate-400 text-xs">Navigate with:</span>
                  <Button
                    size="sm"
                    variant={preferredNav === "google" ? "default" : "outline"}
                    className={preferredNav === "google" ? "bg-cyan-500 text-white" : "border-slate-600 text-slate-400"}
                    onClick={() => setNavPreference("google")}
                  >
                    Google Maps
                  </Button>
                  <Button
                    size="sm"
                    variant={preferredNav === "waze" ? "default" : "outline"}
                    className={preferredNav === "waze" ? "bg-cyan-500 text-white" : "border-slate-600 text-slate-400"}
                    onClick={() => setNavPreference("waze")}
                  >
                    Waze
                  </Button>
                </div>

                {/* Location Notes */}
                {currentRide.status === "in_progress" && (
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Add notes for client (e.g., 'Parked outside gate')"
                      value={locationNotes}
                      onChange={(e) => setLocationNotes(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      rows={2}
                      data-testid="location-notes-input"
                    />
                    <Button
                      onClick={handleUpdateLocation}
                      disabled={isLoading}
                      className="bg-slate-800 hover:bg-slate-700 border border-slate-700"
                      data-testid="update-location-button"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Trip Controls */}
                <div className="pt-2">
                  <TripControls
                    ride={currentRide}
                    isLoading={isLoading}
                    onAction={handleTripAction}
                    onNavigate={openNavigation}
                    onCashReceived={() => handleConfirmCashReceived(currentRide.id)}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* My Schedule - Assigned rides from dispatch */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold">My Schedule</h2>
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            {scheduledRides.length} upcoming
          </Badge>
        </div>

        {scheduledRides.length === 0 && !currentRide ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No scheduled rides</h3>
              <p className="text-slate-400">Dispatch will assign rides to you</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {scheduledRides.map((ride) => {
              const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
              return (
                <Card 
                  key={ride.id} 
                  className="bg-slate-900 border-slate-800"
                  data-testid={`scheduled-ride-${ride.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center">
                          <VehicleIcon className="w-6 h-6 text-lime-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg text-white">{ride.client_name}</p>
                          <p className="text-slate-400 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {ride.client_phone}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-purple-500/20 text-purple-400">Scheduled</Badge>
                    </div>
                    
                    <div className="bg-slate-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-lime-400 font-semibold mb-2">
                        <Calendar className="w-4 h-4" />
                        {ride.pickup_date} at {ride.pickup_time}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300">{ride.pickup_locations[0]?.address}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300">{ride.dropoff_address}</span>
                        </div>
                      </div>
                    </div>

                    {/* CLIENT NEEDS - Critical Info for Driver */}
                    {(ride.mobility_aids?.length > 0 || ride.assistance_needs?.length > 0) && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                        {ride.mobility_aids?.length > 0 && (
                          <p className="text-amber-300 text-sm mb-2">
                            <Accessibility className="w-4 h-4 inline mr-1" />
                            <span className="font-semibold">Client has:</span>{' '}
                            {ride.mobility_aids.map(aid => {
                              const labels = {
                                'wheelchair': 'Wheelchair',
                                'cane': 'Cane',
                                'walker': 'Walker',
                                'crutches': 'Crutches',
                                'motorized_wheelchair': 'Motorized Wheelchair',
                                'scooter': 'Scooter'
                              };
                              return labels[aid] || aid.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                            }).join(', ')}
                          </p>
                        )}
                        {ride.assistance_needs?.length > 0 && (
                          <p className="text-blue-300 text-sm">
                            <span className="font-semibold">Client needs:</span>{' '}
                            {ride.assistance_needs.map(need => {
                              const labels = {
                                'step_stool': 'Step Stool',
                                'entry_exit_help': 'Help Getting In/Out of Vehicle',
                                'blind': 'Visual Assistance',
                                'deaf': 'Hearing Assistance',
                                'oxygen': 'Oxygen Tank Assistance',
                                'service_animal': 'Service Animal Accommodation'
                              };
                              return labels[need] || need.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                            }).join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Ride Sharing Preference */}
                    {ride.ride_sharing_preference && (
                      <div className={`flex items-center gap-2 p-2 rounded-lg mb-3 ${
                        ride.ride_sharing_preference === 'shared' 
                          ? 'bg-green-500/20' 
                          : 'bg-rose-500/20'
                      }`}>
                        <span className={`text-sm font-medium ${
                          ride.ride_sharing_preference === 'shared' ? 'text-green-300' : 'text-rose-300'
                        }`}>
                          {ride.ride_sharing_preference === 'shared' 
                            ? '👥 OK to share ride' 
                            : '👤 Prefers alone'}
                        </span>
                      </div>
                    )}

                    {ride.special_requests && (
                      <div className="flex items-start gap-2 p-3 bg-slate-800 rounded-lg mb-4">
                        <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-300">{ride.special_requests}</span>
                      </div>
                    )}

                    {/* Cash Payment Alert for Drivers */}
                    {ride.payment_method === "cash" && ride.payment_status !== "cash_received" && (
                      <div className="p-4 bg-amber-500/20 border-2 border-amber-500/50 rounded-xl mb-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Banknote className="w-6 h-6 text-amber-400" />
                          <p className="font-bold text-amber-300 text-lg">CASH PAYMENT</p>
                        </div>
                        <p className="text-amber-200 text-sm mb-3">
                          Collect <span className="font-bold text-white">${ride.fare_estimate?.toFixed(2) || "0.00"}</span> in cash from the client <span className="font-bold">BEFORE</span> starting this trip.
                        </p>
                        <p className="text-xs text-amber-300/80 italic">
                          You cannot start this trip until you confirm cash has been received.
                        </p>
                      </div>
                    )}

                    {/* Private Ride Info - Comprehensive Display for Driver */}
                    {ride.ride_type === "private" && (
                      <div className="p-4 bg-orange-500/20 border border-orange-500/30 rounded-lg mb-4 space-y-3" data-testid={`private-ride-info-${ride.id}`}>
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-orange-500/30">
                          <Car className="w-5 h-5 text-orange-400" />
                          <span className="font-bold text-orange-300 text-lg">Private Ride Details</span>
                          <Badge className={`ml-auto ${ride.service_type === "round_trip" ? "bg-orange-500/40" : "bg-blue-500/40"} text-white border-0`}>
                            {ride.service_type === "round_trip" ? "Round Trip" : "One Way"}
                          </Badge>
                        </div>
                        
                        {/* Trip Purpose (for round trips) */}
                        {ride.trip_purpose && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Trip Purpose:</span>
                            <span className="text-orange-200 font-medium capitalize">
                              {ride.trip_purpose === "errands_multiple" ? "Errands / Multiple Stops" : ride.trip_purpose.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                        
                        {/* Wait & Return */}
                        {ride.wait_and_return && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Wait & Return:</span>
                            <span className="text-orange-200 font-medium">Yes - ~{ride.wait_time} minutes</span>
                          </div>
                        )}
                        
                        {/* Additional Stops */}
                        {ride.stops?.filter(s => s.address).length > 0 && (
                          <div className="text-sm">
                            <span className="text-slate-400">Additional Stops:</span>
                            <div className="mt-1 space-y-1">
                              {ride.stops.filter(s => s.address).map((stop, idx) => (
                                <div key={idx} className="text-orange-200 pl-4">
                                  {idx + 1}. {stop.address}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Assistance Getting In/Out */}
                        {ride.needs_entry_assistance !== null && ride.needs_entry_assistance !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Needs Entry/Exit Help:</span>
                            <span className={`font-medium ${ride.needs_entry_assistance ? "text-amber-300" : "text-green-300"}`}>
                              {ride.needs_entry_assistance ? "Yes" : "No"}
                            </span>
                          </div>
                        )}
                        
                        {/* Mobility Aid */}
                        {ride.mobility_aid && ride.mobility_aid !== "none" && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Mobility Aid:</span>
                            <span className="text-amber-300 font-medium capitalize">
                              {ride.mobility_aid === "other" && ride.mobility_aid_other 
                                ? ride.mobility_aid_other 
                                : ride.mobility_aid}
                            </span>
                          </div>
                        )}
                        
                        {/* Accessibility Need */}
                        {ride.accessibility_need && ride.accessibility_need !== "none" && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Accessibility Need:</span>
                            <span className="text-blue-300 font-medium">
                              {ride.accessibility_need === "hearing_impaired" && "Hearing Impaired / Deaf"}
                              {ride.accessibility_need === "visually_impaired" && "Blind / Visually Impaired"}
                              {ride.accessibility_need === "other" && (ride.accessibility_other || "Other")}
                            </span>
                          </div>
                        )}
                        
                        {/* Service Animal */}
                        {ride.has_service_animal !== null && ride.has_service_animal !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Service Animal:</span>
                            <span className={`font-medium ${ride.has_service_animal ? "text-amber-300" : "text-green-300"}`}>
                              {ride.has_service_animal ? "Yes" : "No"}
                            </span>
                          </div>
                        )}
                        
                        {/* Client Notes */}
                        {ride.special_requests && (
                          <div className="text-sm">
                            <span className="text-slate-400">Client Notes:</span>
                            <p className="text-orange-200 mt-1 pl-4 italic">{ride.special_requests}</p>
                          </div>
                        )}
                        
                        {/* Vehicle Type */}
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Vehicle Type:</span>
                          <span className="text-orange-200 font-medium capitalize">{ride.vehicle_type || "Car"}</span>
                        </div>
                        
                        {/* Payment Method */}
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Payment Method:</span>
                          <span className={`font-medium capitalize ${ride.payment_method === "cash" ? "text-amber-300" : "text-green-300"}`}>
                            {ride.payment_method === "cashapp" ? "Cash App" : 
                             ride.payment_method === "applepay" ? "Apple Pay" : 
                             ride.payment_method || "Card"}
                          </span>
                        </div>
                        
                        {/* Total Ride Price - Prominent */}
                        <div className="mt-3 pt-3 border-t border-orange-500/30 flex justify-between items-center">
                          <span className="text-white font-bold text-lg">Total Ride Price:</span>
                          <span className="text-2xl font-bold text-orange-300">${ride.fare_estimate?.toFixed(2) || "0.00"}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-xs">Client PIN</p>
                        <p className="font-mono font-bold text-lime-400 tracking-wider">{ride.pin_code}</p>
                      </div>
                      
                      {ride.trip_phase === "accepted" ? (
                        <Button
                          onClick={() => handleStartRide(ride)}
                          disabled={isLoading || !!currentRide}
                          className="bg-cyan-400 hover:bg-cyan-500 text-slate-900 font-bold px-4"
                          data-testid={`start-ride-${ride.id}`}
                        >
                          <Navigation className="w-4 h-4 mr-2" /> Navigate to Pickup
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleAcceptRide(ride.id)}
                          disabled={isLoading}
                          className="bg-lime-400 hover:bg-lime-500 text-slate-900 font-bold px-6"
                          data-testid={`accept-ride-${ride.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Accept Ride
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* My Calendar - only this driver's assigned rides */}
        <div className="mt-8" data-testid="driver-calendar-section">
          <h2 className="text-xl font-heading font-bold mb-4">My Calendar</h2>
          <CalendarView variant="dark" defaultView="list" audience="driver" onOpenRide={setCalendarRideId} testId="driver-calendar" />
        </div>
        <RideDetailDialog rideId={calendarRideId} onClose={() => setCalendarRideId(null)} mode="driver" />

        {/* Completed Rides */}
        {completedRides.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-heading font-bold mb-4 text-slate-400">Completed Today</h2>
            <div className="space-y-2">
              {completedRides.slice(0, 5).map((ride) => (
                <div 
                  key={ride.id}
                  className="flex items-center justify-between p-3 bg-slate-900 border border-slate-800 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-white">{ride.client_name}</p>
                    <p className="text-slate-500 text-sm">{ride.pickup_time}</p>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400">Completed</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 safe-area-bottom z-40">
        <div className="flex items-center justify-around py-2">
          <button className="flex flex-col items-center gap-1 p-2 text-lime-400">
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-medium">Schedule</span>
          </button>
          <button 
            onClick={() => setShowNotifications(true)}
            className="flex flex-col items-center gap-1 p-2 text-slate-500 relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-2 w-4 h-4 bg-lime-400 text-slate-900 text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount}
              </span>
            )}
            <span className="text-xs font-medium">Alerts</span>
          </button>
          <button 
            onClick={logout}
            className="flex flex-col items-center gap-1 p-2 text-slate-500"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-xs font-medium">Logout</span>
          </button>
        </div>
      </nav>

      {/* Notifications Panel */}
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)}
        variant="dark"
      />

      {/* SMS Log Panel */}
      <SMSLogPanel 
        isOpen={showSMSLog} 
        onClose={() => setShowSMSLog(false)} 
      />

      {/* My Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-400" />
                  My Performance
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowStats(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <AlertCircle className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {myStats ? (
                <>
                  {/* Rank */}
                  <div className="text-center p-4 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl border border-amber-500/30">
                    {myStats.is_driver_of_month && (
                      <div className="text-amber-400 text-sm font-bold mb-1">🏆 Driver of the Month!</div>
                    )}
                    <p className="text-slate-400 text-sm">Your Rank</p>
                    <p className="text-5xl font-black text-white">#{myStats.rank}</p>
                    <p className="text-slate-400 text-sm">of {myStats.total_drivers} drivers</p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-lime-400">{myStats.completed_rides}</p>
                      <p className="text-slate-400 text-xs">Completed Rides</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-400">{myStats.on_time_percent}%</p>
                      <p className="text-slate-400 text-xs">On-Time Rate</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-400 flex items-center justify-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400" />{myStats.rating}
                      </p>
                      <p className="text-slate-400 text-xs">Rating</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">{myStats.client_needs_rides}</p>
                      <p className="text-slate-400 text-xs">Special Needs Rides</p>
                    </div>
                  </div>

                  {/* Badges */}
                  {myStats.badges?.length > 0 && (
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Badges Earned</p>
                      <div className="flex flex-wrap gap-2">
                        {myStats.badges.map((badge, i) => (
                          <div key={i} className="bg-slate-700 px-3 py-1.5 rounded-full flex items-center gap-2">
                            <span className="text-lg">{badge.emoji}</span>
                            <span className="text-white text-sm">{badge.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Badge Progress */}
                  {myStats.next_badge && (
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-sm">Next Badge: {myStats.next_badge.emoji} {myStats.next_badge.name}</span>
                        <span className="text-white text-sm">{myStats.next_badge.progress}/{myStats.next_badge.target}</span>
                      </div>
                      <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                          style={{ width: `${(myStats.next_badge.progress / myStats.next_badge.target) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-lime-400 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-400">Loading your stats...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confetti Celebration on Ride Complete */}
      <ConfettiCelebration
        active={showCelebration}
        message="🎉 Great Job!"
        subMessage={celebrationMessage}
        onComplete={() => {
          setShowCelebration(false);
          toast.success("Ride completed successfully!");
        }}
      />
    </div>
  );
}
